import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { Badge } from "@/components/common/DataTable";
import { aiCopilotService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ScrollText, ShieldCheck, ShieldAlert } from "lucide-react";

const CONF_COLOR = { High: "green", Medium: "amber", Low: "red" };

export default function AiAuditPage() {
  const { user, hasPermission } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const canAudit = hasPermission("AI.AUDIT");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");

  const load = useCallback(async () => {
    if (!canAudit) { setLoading(false); return; }
    if (isSuper && !tenantId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    try {
      const d = await aiCopilotService.audit({ tenantId: tenantId || undefined, size: 100 });
      setRows(d.items || []);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [isSuper, tenantId, canAudit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper && canAudit) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper, canAudit]);

  if (!canAudit) {
    return (
      <div data-testid="ai-audit-page">
        <PageHeader title="AI Audit Log" subtitle="Every AI Copilot response is logged with its confidence, model and data scope" />
        <div className="text-center py-20 text-muted-foreground" data-testid="ai-audit-denied">
          <ShieldAlert className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">You don't have permission to view the AI audit log (requires AI.AUDIT).</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="ai-audit-page">
      <PageHeader title="AI Audit Log" subtitle="Every AI Copilot response is logged with its confidence, model and data scope" />
      {isSuper && (
        <div className="mb-4">
          <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="ai-audit-tenant">
            <option value="">Select tenant…</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
          </select>
        </div>
      )}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="ai-audit-empty">
          <ScrollText className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">{isSuper && !tenantId ? "Select a tenant to view its AI response log." : "No AI responses logged yet."}</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="ai-audit-list">
          {rows.map((r) => (
            <div key={r.messageId} className="bg-card border border-border rounded-xl p-4" data-testid={`ai-audit-row-${r.messageId}`}>
              <div className="flex items-start justify-between gap-3 mb-1">
                <p className="text-sm font-medium text-foreground">{r.question || "(no question captured)"}</p>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge color={CONF_COLOR[r.confidenceLabel] || "slate"}><ShieldCheck className="h-3 w-3 mr-1 inline" />{r.confidenceLabel || "—"}</Badge>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{r.answer}</p>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="bg-muted rounded-full px-2 py-0.5">{r.model}</span>
                {(r.usedDomains || []).map((d) => <span key={d} className="bg-primary/10 text-primary rounded-full px-2 py-0.5">{d}</span>)}
                {(r.blockedDomains || []).map((d) => <span key={d} className="bg-amber-500/10 text-amber-600 rounded-full px-2 py-0.5">blocked: {d}</span>)}
                <span className="ml-auto">{(r.createdOn || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
